import { useEffect, useState, useRef } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getFeedbackSummary, type InsightDoc } from '@/dao/firestore/FeedbackSummaryRepository';
import { cn } from '@/lib/utils';

function toSeconds(v: any): number {
  const n = Number(v);
  if (isNaN(n)) return 0;
  return n > 1000 ? Math.round(n / 1000) : Math.round(n);
}

interface InsightsListProps {
  feedbackId: string;
  currentTime: number;
}

const getInsightEmoji = (type: string) => {
  switch (type) {
    case 'like': return '👍';
    case 'dislike': return '👎';
    case 'worst': return '💩';
    case 'best': return '⭐';
    default: return '💬';
  }
};

const getTypeLabel = (type: string) => {
  switch (type) {
    case 'like': return 'Positive';
    case 'dislike': return 'Negative';
    case 'worst': return 'Needs improvement';
    case 'best': return 'Highlight';
    default: return 'Comment';
  }
};

export default function InsightsList({ feedbackId, currentTime }: InsightsListProps) {
  const [insights, setInsights] = useState<InsightDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const insightRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const loadInsights = async () => {
      if (!feedbackId) return;
      setLoading(true);
      try {
        const summary = await getFeedbackSummary(feedbackId);
        const loadedInsights = summary?.insights || [];
        setInsights(loadedInsights);
        insightRefs.current = new Array(loadedInsights.length);
      } catch (error) {
        console.error('Error loading insights:', error);
      } finally {
        setLoading(false);
      }
    };
    loadInsights();
  }, [feedbackId]);

  useEffect(() => {
    const activeIndex = insights.findIndex(isInsightActive);
    if (activeIndex !== -1 && insightRefs.current[activeIndex]) {
      insightRefs.current[activeIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [currentTime, insights]);

  const isInsightActive = (insight: InsightDoc) => {
    const start = parseFloat(insight.start);
    const end = Math.min(parseFloat(insight.end), start + 5);
    return currentTime >= start && currentTime <= end;
  };

  if (loading) {
    return (
      <div className="space-y-4 p-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-start space-x-3 p-3 rounded-lg border animate-pulse">
            <div className="w-10 h-10 bg-muted rounded-full" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-muted rounded w-1/4" />
              <div className="h-4 bg-muted rounded w-3/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (insights.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p>No insights available yet</p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <ScrollArea className="h-full" ref={scrollAreaRef}>
        <div className="space-y-3 p-4">
          {insights.map((insight, index) => {
            const isActive = isInsightActive(insight);
            return (
              <Tooltip key={index}>
                <TooltipTrigger asChild>
                  <div
                    ref={(el) => (insightRefs.current[index] = el)}
                    className={cn(
                      "flex items-start space-x-4 p-3 rounded-lg border transition-all duration-300 cursor-default",
                      isActive
                        ? "border-primary bg-primary/5 shadow-md ring-1 ring-primary/20 opacity-100"
                        : "border-border bg-card hover:bg-muted/50 opacity-70"
                    )}
                  >
                    <div className="relative flex-shrink-0">
                      <span className="text-5xl block leading-none">{getInsightEmoji(insight.type)}</span>
                      <Avatar className="w-7 h-7 absolute -bottom-2 -right-2 border-2 border-background shadow-sm">
                        <AvatarImage src={insight.personaImg} alt={insight.personaName} />
                        <AvatarFallback className="text-[10px]">{(insight.personaName || '?').charAt(0)}</AvatarFallback>
                      </Avatar>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-sm">{insight.personaName}</span>
                        <span className={cn(
                          "text-xs px-1.5 py-0.5 rounded",
                          insight.type === 'best' ? "bg-yellow-100 text-yellow-800" :
                          insight.type === 'worst' ? "bg-red-100 text-red-800" :
                          insight.type === 'like' ? "bg-green-100 text-green-800" :
                          insight.type === 'dislike' ? "bg-orange-100 text-orange-800" :
                          "bg-gray-100 text-gray-800"
                        )}>
                          {getTypeLabel(insight.type)}
                        </span>
                        <span className="text-xs text-muted-foreground ml-auto">
                          {toSeconds(insight.start)}s - {toSeconds(insight.end)}s
                        </span>
                      </div>
                      <p className="text-sm text-foreground leading-relaxed">
                        {insight.comment}
                      </p>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  Comment by {insight.personaName || 'Unknown'}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </ScrollArea>
    </TooltipProvider>
  );
}
